export interface CampaignColumnDef {
    key: string;
    label: string;
    defaultVisible: boolean;
    locked?: boolean;
    fixedLast?: boolean;
}

export const CAMPAIGN_COLUMNS: CampaignColumnDef[] = [
    {key: 'code', label: 'Code', defaultVisible: true, locked: true},
    {key: 'name', label: 'Nom', defaultVisible: true, locked: true},
    {key: 'flux', label: 'Flux', defaultVisible: true, locked: true},
    {key: 'reference', label: 'Référence', defaultVisible: true},
    {key: 'type', label: 'Type', defaultVisible: false},
    {key: 'source', label: 'Source', defaultVisible: false},
    {key: 'destination', label: 'Destinataire', defaultVisible: false},
    {key: 'provider', label: 'Provider', defaultVisible: false},
    {key: 'product', label: 'Produit', defaultVisible: true},
    {key: 'support', label: 'Support', defaultVisible: false},
    {key: 'dateStart', label: 'Date de début', defaultVisible: true},
    {key: 'dateEnd', label: 'Date de fin', defaultVisible: true},
    {key: 'objectiveUnits', label: 'Objectif (unités)', defaultVisible: true},
    {key: 'objectiveBudgetMax', label: 'Objectif budget max', defaultVisible: true},
    {key: 'campaignGroups', label: 'Groupes de campagnes', defaultVisible: true},
    {key: 'status', label: 'Statut', defaultVisible: true, locked: true, fixedLast: true}
];

const COLUMN_KEYS = CAMPAIGN_COLUMNS.map((column) => column.key);
const NEW_DEFAULT_COLUMN_KEYS = ['campaignGroups'];

export function getDefaultVisibleColumnKeys(): string[] {
    return CAMPAIGN_COLUMNS.filter((column) => column.defaultVisible).map((column) => column.key);
}

export function getDefaultColumnOrder(): string[] {
    return [...COLUMN_KEYS];
}

export function getLockedColumnKeys(): string[] {
    return CAMPAIGN_COLUMNS.filter((column) => column.locked).map((column) => column.key);
}

export function getFixedLastColumnKeys(): string[] {
    return CAMPAIGN_COLUMNS.filter((column) => column.fixedLast).map((column) => column.key);
}

function moveFixedLastColumnsToEnd(ordered: string[], visible: Set<string>): string[] {
    const result = [...ordered];
    getFixedLastColumnKeys().forEach((key) => {
        if (!visible.has(key)) {
            return;
        }
        const index = result.indexOf(key);
        if (index >= 0) {
            result.splice(index, 1);
            result.push(key);
        }
    });
    return result;
}

export function resolveCampaignColumnLayout(
    visibleColumns?: string[] | null,
    columnOrder?: string[] | null
): { visibleColumnKeys: string[]; orderedColumnKeys: string[] } {
    const lockedKeys = getLockedColumnKeys();
    const knownKeys = new Set(COLUMN_KEYS);

    const visible = new Set<string>();
    (visibleColumns || getDefaultVisibleColumnKeys()).forEach((key) => {
        if (knownKeys.has(key)) {
            visible.add(key);
        }
    });
    lockedKeys.forEach((key) => visible.add(key));
    if (visibleColumns?.length) {
        NEW_DEFAULT_COLUMN_KEYS.forEach((key) => {
            if (knownKeys.has(key) && !visibleColumns.includes(key)) {
                visible.add(key);
            }
        });
    }

    const orderSource = (columnOrder && columnOrder.length > 0) ? columnOrder : getDefaultColumnOrder();
    const ordered: string[] = [];
    orderSource.forEach((key) => {
        if (knownKeys.has(key) && visible.has(key) && !ordered.includes(key)) {
            ordered.push(key);
        }
    });
    [...visible].forEach((key) => {
        if (!ordered.includes(key)) {
            const defaultIndex = getDefaultColumnOrder().indexOf(key);
            if (defaultIndex > 0) {
                const anchor = getDefaultColumnOrder()[defaultIndex - 1];
                const anchorIndex = ordered.indexOf(anchor);
                if (anchorIndex >= 0) {
                    ordered.splice(anchorIndex + 1, 0, key);
                    return;
                }
            }
            ordered.push(key);
        }
    });

    const orderedColumnKeys = moveFixedLastColumnsToEnd(ordered, visible);

    return {
        visibleColumnKeys: [...visible],
        orderedColumnKeys
    };
}
